/**
 * Effect graph builders + shared curve helpers. Extracted from
 * `createFxBus` in `useAudioEngine.ts` so the same factories can be
 * reused by per-bus FX chains (Phase 8b).
 *
 * Each `build*Fx(ctx)` function returns the internal node handles
 * the caller needs — both the signal I/O (`input` / `output` /
 * `wet`) and the kind-specific params the caller will automate
 * (e.g. `delay.feedback`, `crush.node`). Keeping the internals
 * exposed means the master FX bank's low-pass filter + tape-stop
 * coupling behaviour stays intact in `createFxBus` with no
 * semantic change.
 *
 * This is a pure refactor — the graph + node counts + starting
 * values are identical to what `createFxBus` used to build inline.
 * The shared curves + impulse response are hoisted here too so
 * the master bus and future per-bus chains don't each compute
 * their own.
 */

// ── Shared curve helpers ──────────────────────────────────────

/**
 * Exponentially-decaying white-noise impulse response. Fed to a
 * ConvolverNode for the reverb effect — sounds like a small plate
 * when driven by short percussive sources, and cheap to compute.
 *
 * Called once per bus instance — the resulting AudioBuffer is
 * reusable across engages, so there's no need to rebuild it each
 * time the reverb engages.
 */
export function createImpulseResponse(
  ctx: AudioContext,
  durationSec: number,
  decay: number,
): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * durationSec)
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate)
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch)
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay)
    }
  }
  return impulse
}

/** Identity `y = x` curve for a `WaveShaperNode` — acts as
 *  transparent pass-through when the crush effect is bypassed.
 *  Shared instance; the engine swaps the curve reference rather
 *  than allocating a fresh one per toggle. */
export function identityCurve(): Float32Array {
  const n = 1024
  const curve = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    curve[i] = (i / (n - 1)) * 2 - 1
  }
  return curve
}

/** Step-quantized curve for the bitcrush effect. `bits` is the
 *  number of quantization levels (default 4 = audible crunch,
 *  8 = subtle). */
export function bitcrushCurve(bits = 4): Float32Array {
  const n = 1024
  const curve = new Float32Array(n)
  const levels = Math.pow(2, bits)
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1
    curve[i] = Math.round(x * levels) / levels
  }
  return curve
}

// ── Per-effect graph builders ────────────────────────────────
//
// Every builder constructs a self-contained sub-graph for one
// effect. The caller wires the input / output into the surrounding
// bus, and sets / ramps the exposed AudioParams to engage / release.

/** Master-style master-filter node: a single biquad whose `type`
 *  and `frequency` are manipulated by the engage code to produce
 *  the unipolar low-pass sweep + tape-stop muffle. */
export interface MasterFilterFx {
  /** Serial filter — both input and output are the same node;
   *  callers wire `input` upstream + route `output` downstream. */
  input: BiquadFilterNode
  output: BiquadFilterNode
  filter: BiquadFilterNode
}

export function buildMasterFilterFx(ctx: AudioContext): MasterFilterFx {
  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  // Bypass value — 22 kHz is effectively transparent for human
  // hearing, so the filter pass-through-equivalent state is
  // "lowpass at 22 kHz" rather than a separate bypass branch.
  filter.frequency.value = 22000
  return { input: filter, output: filter, filter }
}

/** Delay line with feedback — classic send-style effect.
 *
 *  Optional biquad in the wet path so kick + bass frequencies don't
 *  muddy the echoes. Defaults to a 200 Hz high-pass on delay (and
 *  off on stutter, configured by the caller). `filter.type`
 *  supports `'highpass'` and `'lowpass'` via `setFilterMode` /
 *  Bus applyKindParams — no separate bypass branch; type = 'allpass'
 *  is the off state so the signal chain stays in place. */
export interface DelayFx {
  /** Upstream signal routes here. */
  input: DelayNode
  /** Downstream consumers read from this gain node (dry-bypassed). */
  output: GainNode
  delay: DelayNode
  feedback: GainNode
  /** Biquad in the feedback loop — same filter shapes the returning
   *  taps and the wet-send tap, so each echo is progressively more
   *  filtered (natural darkening over time). */
  filter: BiquadFilterNode
  wet: GainNode
}

export function buildDelayFx(ctx: AudioContext): DelayFx {
  const delay = ctx.createDelay(1.0)
  delay.delayTime.value = 0.25 // dotted-eighth at 120 BPM
  const filter = ctx.createBiquadFilter()
  filter.type = 'highpass'
  filter.frequency.value = 200 // cuts kick + bass from repeats
  filter.Q.value = 0.707 // Butterworth-ish; musical, no resonance
  const feedback = ctx.createGain()
  feedback.gain.value = 0 // bypass: no feedback
  // Route the delay through the filter before feedback + wet tap so
  // the HP/LP shapes both the feedback loop and what reaches the mix.
  delay.connect(filter)
  filter.connect(feedback).connect(delay)
  const wet = ctx.createGain()
  wet.gain.value = 0
  filter.connect(wet)
  return { input: delay, output: wet, delay, feedback, filter, wet }
}

/** Convolver-based reverb. Fixed impulse response — the effect's
 *  character is shaped by wet gain only; no diffusion / size param
 *  in this MVP. */
export interface ReverbFx {
  input: ConvolverNode
  output: GainNode
  convolver: ConvolverNode
  wet: GainNode
}

export function buildReverbFx(ctx: AudioContext): ReverbFx {
  const convolver = ctx.createConvolver()
  convolver.buffer = createImpulseResponse(ctx, 1.5, 2.5)
  const wet = ctx.createGain()
  wet.gain.value = 0
  convolver.connect(wet)
  return { input: convolver, output: wet, convolver, wet }
}

/** Bitcrush — WaveShaperNode with a step-quantized curve. Swap
 *  between `identityCurve` (bypass) and `bitcrushCurve` (engaged)
 *  via `shaper.curve = …` without rebuilding the node. */
export interface BitcrushFx {
  input: WaveShaperNode
  output: GainNode
  shaper: WaveShaperNode
  wet: GainNode
}

export function buildBitcrushFx(ctx: AudioContext): BitcrushFx {
  const shaper = ctx.createWaveShaper()
  shaper.curve = identityCurve()
  shaper.oversample = 'none' // keep the quantization audible
  const wet = ctx.createGain()
  wet.gain.value = 0
  shaper.connect(wet)
  return { input: shaper, output: wet, shaper, wet }
}

/** Stutter — short delay + near-unity feedback creates a holding
 *  loop that sustains the last ~80 ms fragment while engaged.
 *
 *  Same optional wet-path filter as `DelayFx` but defaults to
 *  off — the stuttering snapshot is usually the whole sound, so
 *  filtering it would change the stutter's character. Left
 *  configurable so a dark / resonant stutter recipe is possible. */
export interface StutterFx {
  input: DelayNode
  output: GainNode
  delay: DelayNode
  feedback: GainNode
  filter: BiquadFilterNode
  wet: GainNode
}

export function buildStutterFx(ctx: AudioContext): StutterFx {
  const delay = ctx.createDelay(0.5)
  delay.delayTime.value = 0.08
  const filter = ctx.createBiquadFilter()
  filter.type = 'allpass' // bypass: phase shift only, no coloration
  filter.frequency.value = 200
  filter.Q.value = 0.707
  const feedback = ctx.createGain()
  feedback.gain.value = 0
  delay.connect(filter)
  filter.connect(feedback).connect(delay)
  const wet = ctx.createGain()
  wet.gain.value = 0
  filter.connect(wet)
  return { input: delay, output: wet, delay, feedback, filter, wet }
}

/** Tape-stop — serial effect that couples a short delay line with
 *  a volume ramp at engage-time. The engage code separately drives
 *  the bus's master filter to produce the muffle sweep. */
export interface TapeStopFx {
  /** Upstream routes here. */
  input: DelayNode
  /** Downstream reads from the tape-gain node so the ramp-to-
   *  silence behaviour applies. */
  output: GainNode
  delay: DelayNode
  gain: GainNode
}

export function buildTapeStopFx(ctx: AudioContext): TapeStopFx {
  const delay = ctx.createDelay(0.2)
  delay.delayTime.value = 0
  const gain = ctx.createGain()
  gain.gain.value = 1
  delay.connect(gain)
  return { input: delay, output: gain, delay, gain }
}
